<template>
  <button
    :disabled="loadingSpinnerStore.localLoadingSpinner ? true : disabled"
    :class="classList"
    @click="$emit('click')"
  >
    {{ label }}
    <slot></slot>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { twMerge } from 'tailwind-merge';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';

const loadingSpinnerStore = useLoadingSpinnerStore();

interface TButtonProps {
  label?: string;
  disabled?: boolean;
  color?: 'white' | 'indigo' | 'black' | 'red' | 'amber';
  size?: 'sm' | 'md' | 'lg';
}

const { label, disabled = false, color = 'white', size = 'md' } = defineProps<TButtonProps>();

defineEmits(['click']);

const classList = computed(() => {
  return twMerge(baseClassList, colorClassList.value, disabledClassList.value, sizeClassList.value);
});

const baseClassList =
  'w-full sm:w-auto justify-center inline-flex items-center border font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2';

const colorClassList = computed(() => {
  if (color === 'white') {
    return ` border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-indigo-500`;
  } else if (color === 'indigo') {
    return `border-transparent text-white bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500`;
  } else if (color === 'black') {
    return `border-transparent text-white bg-gray-600 hover:bg-gray-700 focus:ring-gray-500`;
  } else if (color === 'red') {
    return `border-transparent text-white bg-red-600 hover:bg-red-700 focus:ring-red-500`;
  } else if (color === 'amber') {
    return `border-transparent text-slate-900 bg-amber-500 hover:bg-amber-600 focus:ring-slate-900`;
  }
  return '';
});

const disabledClassList = computed(() => {
  return disabled ? 'opacity-30 cursor-not-allowed' : '';
});

const sizeClassList = computed(() => {
  if (size === 'sm') {
    return 'px-2 py-1 text-xs';
  } else if (size === 'md') {
    return 'px-4 py-2 text-sm';
  } else if (size === 'lg') {
    return 'px-6 py-3';
  }
  return '';
});
</script>
